import { ArrowLeft, Shield, Lock, Eye, Database, Bot, Cookie, Mail, FileText } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

export default function PrivacyPage() {
  const navigate = useNavigate();
  const lastUpdate = '10 avril 2026';

  const sections = [
    {
      icon: Shield,
      title: 'Responsable du traitement',
      content: `MediPocket est édité par le Dr Jeremy SEBAG, médecin généraliste.
Pour toute question relative à la protection de vos données, vous pouvez nous contacter à l'adresse : contact@medipocket.fr`
    },
    {
      icon: FileText,
      title: "Objet de l'application",
      content: `MediPocket est un outil d'aide à la rédaction médicale destiné aux professionnels de santé. L'application permet :
- La transcription audio de consultations médicales via reconnaissance vocale (Whisper)
- La structuration automatique de notes médicales par intelligence artificielle (Claude, Anthropic)
- L'extraction de données cliniques depuis Doctolib via une extension Chrome
- La génération de courriers d'adressage, ordonnances et fiches conseils patients
- L'identification automatique de codes de facturation (NGAP/CCAM)`
    },
    {
      icon: Database,
      title: 'Politique "Zero Storage" — Données de santé',
      content: `MediPocket applique une politique stricte de non-stockage des données de santé :

Aucune donnée de santé n'est conservée sur nos serveurs. Les transcriptions, notes médicales, contenus audio et données cliniques sont traités de manière éphémère et ne sont jamais persistés en base de données.

Les données de consultation sont stockées exclusivement dans le navigateur du professionnel de santé (sessionStorage) et sont automatiquement supprimées à la fermeture de l'onglet.

Les fiches conseils patients partagées par lien utilisent un système de compression dans le fragment d'URL (#d=...) qui n'est jamais transmis au serveur. Ces liens expirent automatiquement après 24 heures.

Seules les métadonnées non-identifiantes sont conservées côté serveur : horodatage, type d'opération, crédits consommés.`
    },
    {
      icon: Lock,
      title: 'Données collectées',
      content: `Données de compte (conservées) :
- Adresse email professionnelle
- Nom du praticien
- Rôle et préférences d'accès

Données de consultation (NON conservées — traitement éphémère uniquement) :
- Enregistrements audio (transmis à OpenAI Whisper pour transcription, non stockés)
- Transcriptions textuelles (transmises à Claude pour structuration, non stockées)
- Données cliniques extraites de Doctolib (âge, antécédents, constantes — traitées en mémoire, non stockées)
- Photos médicales (analysées par Claude Vision, non stockées)

Données techniques :
- Logs de connexion et tentatives d'authentification (sécurité)
- Statistiques d'utilisation anonymisées (crédits, opérations)`
    },
    {
      icon: Bot,
      title: "Utilisation de l'intelligence artificielle",
      content: `MediPocket utilise les services d'IA suivants :

OpenAI Whisper — Transcription audio en texte. Les fichiers audio sont transmis de manière chiffrée, traités et immédiatement supprimés. OpenAI ne conserve pas les données transmises via l'API.

Claude (Anthropic) — Structuration des notes médicales, analyse de sécurité, génération de courriers. Les textes sont transmis de manière chiffrée. Anthropic s'engage à ne pas utiliser les données API pour l'entraînement de ses modèles.

Aucune donnée patient nominative (nom, prénom, date de naissance) n'est transmise aux services d'IA. Le contexte clinique est anonymisé avant envoi (âge uniquement, pas de date de naissance ni de nom).`
    },
    {
      icon: Eye,
      title: "Extension Chrome — Données Doctolib",
      content: `L'extension Chrome MediPocket interagit avec Doctolib pour :
- Extraire le contexte clinique du patient (âge, sexe, antécédents, allergies, traitements, constantes vitales)
- Injecter les notes médicales structurées dans les champs Doctolib (interrogatoire, examen, conclusion, motif)
- Enregistrer et transcrire l'audio de la consultation

L'extension ne collecte ni ne transmet :
- Le nom ou prénom du patient
- La date de naissance complète
- Le numéro de sécurité sociale
- Les coordonnées du patient (adresse, téléphone)

L'extension fonctionne exclusivement en local dans le navigateur du praticien. Les données extraites ne transitent par nos serveurs que pour la transcription et la structuration IA, sans aucun stockage.

Permissions Chrome requises :
- activeTab : accès à l'onglet Doctolib actif uniquement
- sidePanel : affichage du panneau latéral MediPocket
- cookies : authentification avec medipocket.fr
- storage : sauvegarde des préférences locales`
    },
    {
      icon: Cookie,
      title: 'Cookies et authentification',
      content: `MediPocket utilise les cookies suivants :

Cookies strictement nécessaires (httpOnly, Secure) :
- access_token : jeton d'authentification JWT (durée : 4 heures)
- refresh_token : renouvellement de session (durée : 7 jours)

L'authentification s'effectue par email et mot de passe, avec un code de vérification à usage unique envoyé par email.

Aucun cookie de traçage, publicitaire ou analytique n'est utilisé.
Aucun service tiers de tracking n'est intégré (pas de Google Analytics, pas de Facebook Pixel, etc.).`
    },
    {
      icon: Shield,
      title: 'Vos droits (RGPD)',
      content: `Conformément au Règlement Général sur la Protection des Données (RGPD) et à la loi Informatique et Libertés, vous disposez des droits suivants :

- Droit d'accès : obtenir une copie de vos données personnelles
- Droit de rectification : corriger vos données inexactes
- Droit à l'effacement : demander la suppression de votre compte et de vos données
- Droit à la portabilité : recevoir vos données dans un format structuré
- Droit d'opposition : vous opposer au traitement de vos données
- Droit à la limitation : restreindre le traitement de vos données

Pour exercer ces droits, contactez-nous à : contact@medipocket.fr

Vous disposez également du droit d'introduire une réclamation auprès de la CNIL (Commission Nationale de l'Informatique et des Libertés) : www.cnil.fr`
    },
    {
      icon: Lock,
      title: 'Sécurité des données',
      content: `Mesures de sécurité mises en œuvre :
- Chiffrement TLS/HTTPS pour toutes les communications
- Authentification par jetons JWT et cookies httpOnly/Secure
- Protection contre les attaques par force brute (verrouillage après 5 tentatives)
- Validation stricte de l'origine des messages (postMessage)
- Aucun stockage de données de santé côté serveur
- Hébergement sécurisé avec accès restreint`
    },
    {
      icon: Mail,
      title: 'Contact',
      content: `Pour toute question relative à cette politique de confidentialité ou à la protection de vos données :

Email : contact@medipocket.fr
Responsable : Dr Jeremy SEBAG

Cette politique de confidentialité peut être mise à jour. La date de dernière modification est indiquée en haut de cette page.`
    },
  ];

  return (
    <div className="min-h-screen bg-[#f8fafc]">
      {/* Header */}
      <header className="sticky top-0 z-40 bg-white/80 backdrop-blur-xl border-b border-slate-200/50">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 h-14 flex items-center justify-between">
          <div className="flex items-center gap-2.5">
            <div className="w-8 h-8 bg-gradient-to-br from-indigo-500 to-violet-500 rounded-lg flex items-center justify-center">
              <Shield className="w-4 h-4 text-white" />
            </div>
            <h1 className="text-lg font-semibold font-['Outfit'] bg-gradient-to-r from-indigo-500 to-violet-500 bg-clip-text text-transparent tracking-tight">MediPocket</h1>
            <span className="text-sm text-slate-400 ml-1">/ Confidentialité</span>
          </div>
          <button
            onClick={() => navigate('/')}
            className="flex items-center gap-1.5 text-sm text-slate-500 hover:text-slate-700 transition-colors"
            data-testid="btn-back-privacy"
          >
            <ArrowLeft className="w-4 h-4" />
            Retour
          </button>
        </div>
      </header>

      {/* Content */}
      <main className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-10" data-testid="privacy-page">
        {/* Title */}
        <div className="mb-10">
          <h1 className="text-3xl sm:text-4xl font-bold text-slate-900 font-['Outfit'] tracking-tight mb-3">
            Politique de confidentialité
          </h1>
          <p className="text-slate-500 text-sm">
            Dernière mise à jour : {lastUpdate}
          </p>
        </div>

        {/* Summary card */}
        <div className="mb-8 p-5 rounded-2xl bg-gradient-to-r from-emerald-50 to-teal-50 border border-emerald-200/60">
          <div className="flex items-start gap-3">
            <div className="w-10 h-10 rounded-xl bg-emerald-100 flex items-center justify-center flex-shrink-0 mt-0.5">
              <Database className="w-5 h-5 text-emerald-600" />
            </div>
            <div>
              <h2 className="text-base font-semibold text-emerald-800 mb-1">Zero Storage — Aucune donnée de santé conservée</h2>
              <p className="text-sm text-emerald-700 leading-relaxed">
                MediPocket ne stocke aucune donnée de santé sur ses serveurs. Les transcriptions et notes médicales sont traitées de manière éphémère et restent exclusivement dans le navigateur du praticien.
              </p>
            </div>
          </div>
        </div>

        {/* Sections */}
        <div className="space-y-6">
          {sections.map((section) => {
            const Icon = section.icon;
            return (
              <section
                key={section.title}
                className="bg-white rounded-2xl border border-slate-200/60 shadow-[0_2px_10px_-3px_rgba(6,81,237,0.04)] overflow-hidden"
                data-testid={`privacy-section-${section.title.toLowerCase().replace(/\s+/g, '-')}`}
              >
                <div className="px-6 py-4 border-b border-slate-100 bg-slate-50/50 flex items-center gap-3">
                  <div className="w-8 h-8 rounded-lg bg-indigo-50 flex items-center justify-center">
                    <Icon className="w-4 h-4 text-indigo-500" />
                  </div>
                  <h2 className="text-base font-semibold text-slate-800 font-['Outfit']">{section.title}</h2>
                </div>
                <div className="px-6 py-5">
                  <div className="text-sm text-slate-600 leading-relaxed whitespace-pre-line">
                    {section.content}
                  </div>
                </div>
              </section>
            );
          })}
        </div>

        {/* Footer */}
        <div className="mt-12 pb-8 text-center">
          <p className="text-xs text-slate-400">
            MediPocket — Conçu pour la médecine générale — Dr SEBAG
          </p>
        </div>
      </main>
    </div>
  );
}
